/**
 * Global auth context.
 *
 * PRD section 5 requires a global hook exposing `{ telegramUserId, role,
 * isAdmin }`, consumed by route guards and by conditionally-rendered admin UI.
 *
 * The important design point: this is a CACHE OF THE SERVER'S ANSWER, never an
 * authority. `isAdmin` here decides whether to render an admin link — it does
 * not decide whether the admin API will answer. The server re-checks Telegram
 * on every request regardless of what this thinks (PRD section 5.6), so a
 * tampered client gets a prettier menu and exactly zero extra access.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthenticatedUser, MemberRole } from '@vlastiq/contracts';
import { ApiError, fetchMe, postLogout } from './api.ts';
import { clearAuthToken } from '../telegram/auth.ts';

export type AuthStatus =
  | 'loading'
  /** Signed in and confirmed still in the group. */
  | 'authenticated'
  /** No valid session — show the login page. */
  | 'unauthenticated'
  /** Valid session, but Telegram says they are out of the group. */
  | 'not_a_member'
  /** Could not reach Telegram. Distinct from a denial on purpose. */
  | 'unavailable'
  /**
   * Mini App only: the launch data Telegram gave us is too old to exchange.
   *
   * Distinct from every other status because the member did nothing wrong, is
   * still in the group, and RETRYING CANNOT HELP — the cached `initData`
   * carries its original `auth_date` and only reopening the app from Telegram
   * mints a fresh one. Folding it into 'unauthenticated' would show a login
   * page whose OIDC redirect cannot complete inside a webview.
   */
  | 'stale_launch_data';

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthenticatedUser | null;
  telegramUserId: number | null;
  role: MemberRole | null;
  isAdmin: boolean;
  /** Set when status is 'unavailable'. */
  message: string | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const { user: me } = await fetchMe();
      setUser(me);
      setMessage(null);
      setStatus('authenticated');
    } catch (error) {
      setUser(null);
      if (error instanceof ApiError) {
        setMessage(error.message);
        switch (error.code) {
          case 'not_a_member':
            setStatus('not_a_member');
            return;
          case 'telegram_unavailable':
            setStatus('unavailable');
            return;
          case 'unauthenticated':
            setMessage(null);
            setStatus('unauthenticated');
            return;
          case 'invalid_telegram_payload':
            // Only reachable inside a Mini App, from the token exchange.
            setStatus('stale_launch_data');
            return;
          default:
            setStatus('unavailable');
            return;
        }
      }
      // Network failure, server down, offline.
      setMessage('Не удалось связаться с сервером.');
      setStatus('unavailable');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await postLogout();
      /*
        Drop the Mini App's in-memory bearer too.

        The session token is stateless — the server cannot revoke it — so
        without this the app would render the login page while still holding a
        credential every subsequent request would happily send. Unreachable
        today, because `SignOut` renders nothing inside a Mini App (a logout
        there is undone by the next silent re-exchange from the cached
        `initData`, which is why the button is absent rather than broken). It
        is here so the invariant "signed out means holding no credential" is
        true by construction rather than by the accident of a hidden button —
        the moment anyone adds a `MainButton` sign-out, it already holds.
      */
      clearAuthToken();
      setUser(null);
      setMessage(null);
      setStatus('unauthenticated');
    } catch (caught) {
      // Do NOT assume signed-out on failure. The request is what clears the
      // server-side cookie; if it did not land, the session is still live for
      // its full TTL. Showing the login page anyway would let someone walk
      // away believing they had signed out on a shared machine.
      // Re-derive the real state instead of asserting one.
      await refresh();
      /*
        ⚠️ AND THEN SAY SO. Re-deriving is silent by construction: the page
        re-renders as the same signed-in page it already was, which from the
        member's side is a Выйти button that did nothing. Rethrowing is what
        lets `SignOut`'s runner put it in a toast — the failure is not this
        provider's to REPORT, but it is its to raise, and swallowing it here is
        what made "you are still signed in" indistinguishable from "the button
        is broken" on a shared machine.
      */
      throw caught;
    }
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      telegramUserId: user?.telegramUserId ?? null,
      role: user?.role ?? null,
      isAdmin: user?.isAdmin ?? false,
      message,
      refresh,
      logout,
    }),
    [status, user, message, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth() must be used inside <AuthProvider>.');
  }
  return context;
}
